/**
 * FERP PDF Viewer Component (DRR-079)
 *
 * Mantine-wrapped react-pdf viewer with page navigation, zoom, and text search.
 * Configures pdfjs-dist worker for Vite. Loads PDFs from S3 presigned URLs.
 *
 * Supports two variants:
 * - "basic" (default): Original standalone viewer with basic toolbar
 * - "full": Full PDF suite with 3-panel layout, enhanced annotations, undo/redo
 *
 * FERP Constraints:
 * - Mantine v8.x ONLY
 * - Dumb Frontend: viewer is read-only display layer
 * - useAuth() for current user context
 */

import { useState, useCallback, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Stack, Center, Loader, Alert, Text, Paper, Button, Group, ThemeIcon, Badge, List } from '@mantine/core';
import { IconAlertTriangle, IconForms, IconFileTypePdf } from '@tabler/icons-react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import { AnnotationLayer } from './AnnotationLayer';
import { PDFToolbar } from './PDFToolbar';
import { PdfSuiteProvider, usePdfSuite } from './PdfSuiteContext';
import { PdfSuiteShell } from './PdfSuiteShell';
import { EnhancedPdfToolbar } from './EnhancedPdfToolbar';
import { InkCanvas } from './InkCanvas';
import { ShapeOverlay } from './ShapeOverlay';
import { StickyNoteLayer } from './StickyNoteLayer';
import { RedactionToolOverlay } from './RedactionToolOverlay';
import { WatermarkOverlay } from './WatermarkOverlay';
import { TextSearchHighlight } from './TextSearchHighlight';
import { KeyboardShortcutHandler } from './KeyboardShortcutHandler';
import { MiniMap } from './MiniMap';
import './pdf-suite.css';
import type { PdfAnnotation, PdfFeatureTier, AnnotationMode, StampValue, PdfViewerState, WatermarkType, DocumentTrailMeta } from './types';

// Configure PDF.js worker — use react-pdf's bundled pdfjs-dist version
// react-pdf v10 pins pdfjs-dist internally; do NOT add a separate pdfjs-dist dep
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PDFViewerProps {
  /** S3 presigned URL or direct URL to PDF file */
  url: string;
  /** Document ID (for annotation persistence) */
  documentId: string;
  /** Existing annotations loaded from Document.dynamodb_content */
  annotations: PdfAnnotation[];
  /** Callback when annotations are modified */
  onAnnotationsChange: (annotations: PdfAnnotation[]) => void;
  /** Callback to request document signing */
  onRequestSign?: () => void;
  /** Callback to export PDF with flattened annotations */
  onRequestExport?: () => void;
  /** Whether current user can annotate */
  canAnnotate?: boolean;
  /** Whether current user can sign */
  canSign?: boolean;
  /** Viewer variant: "basic" (default) or "full" (3-panel suite) */
  variant?: 'basic' | 'full';
  /** Watermark overlay (for full variant) */
  watermark?: WatermarkType;
  /** Feature tier: 'free' (default) or 'pro' */
  featureTier?: PdfFeatureTier;
  /** Document traceability metadata for the trail panel */
  documentTrailMeta?: DocumentTrailMeta | null;
  /** Original filename — used for XFA detection to suggest known form refs */
  fileName?: string;
  /** Callback when XFA flatten produces a new viewable URL */
  onXfaFlattened?: (flattenedUrl: string, documentId: string) => void;
}

export function PDFViewer({
  variant = 'basic',
  watermark,
  featureTier = 'free',
  documentTrailMeta,
  fileName,
  onXfaFlattened,
  ...props
}: PDFViewerProps) {
  if (variant === 'full') {
    return (
      <PdfSuiteProvider initialAnnotations={props.annotations} watermark={watermark} featureTier={featureTier} documentTrailMeta={documentTrailMeta}>
        <FullPDFViewer {...props} fileName={fileName} onXfaFlattened={onXfaFlattened} />
      </PdfSuiteProvider>
    );
  }
  return <BasicPDFViewer {...props} fileName={fileName} onXfaFlattened={onXfaFlattened} />;
}

// ============================================================================
// XFA Detection Panel — shared by both variants
// ============================================================================

/** Known seed state forms that can be flattened server-side. */
const KNOWN_XFA_FORMS: Record<string, string> = {
  'dhs-6790g.pdf': 'dhs-6790g',
  'dhs-6790g-flat.pdf': 'dhs-6790g-FLAT',
  'dhs-6910.pdf': 'dhs-6910',
};

function resolveFormRef(fileName?: string): string | null {
  if (!fileName) return null;
  const lower = fileName.toLowerCase();
  return KNOWN_XFA_FORMS[lower] ?? null;
}

function XfaDetectedPanel({
  fileName,
  onXfaFlattened,
}: {
  fileName?: string;
  onXfaFlattened?: (flattenedUrl: string, documentId: string) => void;
}) {
  const formRef = resolveFormRef(fileName);

  return (
    <Paper withBorder p="xl">
      <Stack align="center" gap="md">
        <ThemeIcon size={64} variant="light" color="orange" radius="xl">
          <IconForms size={32} />
        </ThemeIcon>
        <div style={{ textAlign: 'center' }}>
          <Group gap="xs" justify="center" mb={4}>
            <Text size="lg" fw={600}>XFA Form Detected</Text>
            <Badge color="orange" variant="light" size="sm">Adobe LiveCycle</Badge>
          </Group>
          <Text size="sm" c="dimmed" maw={500}>
            This PDF uses Adobe XFA (XML Forms Architecture), commonly used by
            state government agencies (e.g., MN DHS forms). Browser PDF viewers
            cannot render XFA content.
          </Text>
        </div>
        <Paper withBorder p="md" bg="var(--mantine-color-gray-0)" maw={460}>
          <Text size="sm" fw={500} mb="xs">FERP can process this form server-side:</Text>
          <List size="sm" spacing={4}>
            <List.Item>Flatten XFA to standard PDF viewable in any browser</List.Item>
            <List.Item>Auto-fill fields from workflow/constellation data</List.Item>
            <List.Item>Lock pre-filled fields while leaving blanks editable</List.Item>
          </List>
        </Paper>
        {formRef ? (
          <Button
            variant="filled"
            color="orange"
            leftSection={<IconFileTypePdf size={16} />}
            onClick={() => {
              // Navigate to the pre-flattened version if available
              const flatRef = `${formRef}-FLAT`;
              // Open the flattened seed form directly
              const seedPath = `/state-forms/${flatRef}.pdf`;
              if (onXfaFlattened) {
                onXfaFlattened(seedPath, flatRef);
              }
            }}
          >
            Flatten &amp; Preview
          </Button>
        ) : (
          <Text size="xs" c="dimmed">
            Upload a known state form (DHS-6790G, DHS-6910) or configure a
            form field map (ffm-*) for this template.
          </Text>
        )}
        {fileName && (
          <Badge variant="light" color="gray" size="sm">
            {fileName}
          </Badge>
        )}
      </Stack>
    </Paper>
  );
}

// ============================================================================
// Basic Variant — preserves original behavior exactly
// ============================================================================

function BasicPDFViewer({
  url,
  annotations,
  onAnnotationsChange,
  onRequestSign,
  onRequestExport,
  canAnnotate = false,
  canSign = false,
  fileName,
  onXfaFlattened,
}: Omit<PDFViewerProps, 'variant' | 'watermark'>) {
  const [viewerState, setViewerState] = useState<PdfViewerState>({
    numPages: 0,
    currentPage: 1,
    scale: 1.0,
    zoomFitMode: 'custom',
    searchQuery: '',
    searchMatches: [],
    currentSearchMatch: 0,
    annotationMode: null,
    selectedStamp: 'APPROVED',
    selectedShape: 'rectangle',
    viewMode: 'single',
    inkColor: '#000000',
    inkStrokeWidth: 2,
  });

  const [loadError, setLoadError] = useState<string | null>(null);
  const [isXfa, setIsXfa] = useState(false);

  const onDocumentLoadSuccess = useCallback(async (pdf: { numPages: number; getMetadata?: () => Promise<{ info?: Record<string, unknown> }> }) => {
    setViewerState((prev) => ({ ...prev, numPages: pdf.numPages }));
    setLoadError(null);

    // Detect XFA forms — pdf.js exposes metadata but not XFA flag directly.
    // Check via internal _pdfInfo or by attempting to read first page text.
    try {
      const pdfDoc = pdf as unknown as { _transport?: { _params?: { disableAutoFetch?: boolean } }; _pdfInfo?: { _xfa?: unknown }; getData?: () => Promise<Uint8Array> };
      // pdf.js sets fingerprints; XFA forms render the fallback text.
      // Heuristic: fetch first page text and check for the known XFA fallback message.
      const proxy = pdf as unknown as { getPage: (n: number) => Promise<{ getTextContent: () => Promise<{ items: Array<{ str: string }> }> }> };
      if (proxy.getPage) {
        const page = await proxy.getPage(1);
        const textContent = await page.getTextContent();
        const text = textContent.items.map((item) => item.str).join(' ');
        if (text.includes('This PDF form cannot be displayed') || text.includes('Open With Adobe Reader')) {
          setIsXfa(true);
        }
      }
    } catch {
      // Detection failed — not critical, just don't show XFA banner
    }
  }, []);

  const onDocumentLoadError = useCallback((error: Error) => {
    setLoadError(error.message);
  }, []);

  const setCurrentPage = useCallback((page: number) => {
    setViewerState((prev) => ({
      ...prev,
      currentPage: Math.max(1, Math.min(page, prev.numPages)),
    }));
  }, []);

  const setScale = useCallback((scale: number) => {
    setViewerState((prev) => ({
      ...prev,
      scale: Math.max(0.25, Math.min(3.0, scale)),
    }));
  }, []);

  const setAnnotationMode = useCallback((mode: AnnotationMode) => {
    setViewerState((prev) => ({ ...prev, annotationMode: mode }));
  }, []);

  const setSelectedStamp = useCallback((stamp: StampValue) => {
    setViewerState((prev) => ({ ...prev, selectedStamp: stamp }));
  }, []);

  const setSearchQuery = useCallback((query: string) => {
    setViewerState((prev) => ({ ...prev, searchQuery: query }));
  }, []);

  const handleAddAnnotation = useCallback(
    (annotation: PdfAnnotation) => {
      onAnnotationsChange([...annotations, annotation]);
    },
    [annotations, onAnnotationsChange],
  );

  const handleRemoveAnnotation = useCallback(
    (annotationId: string) => {
      onAnnotationsChange(annotations.filter((a) => a.id !== annotationId));
    },
    [annotations, onAnnotationsChange],
  );

  if (loadError) {
    return (
      <Alert icon={<IconAlertTriangle size={16} />} color="red" title="PDF Load Error">
        Failed to load PDF: {loadError}
      </Alert>
    );
  }

  // XFA forms can't be rendered by pdf.js — show dedicated panel instead of broken content
  if (isXfa) {
    return <XfaDetectedPanel fileName={fileName} onXfaFlattened={onXfaFlattened} />;
  }

  return (
    <Stack gap={0}>
      <PDFToolbar
        viewerState={viewerState}
        onPageChange={setCurrentPage}
        onZoomChange={setScale}
        onAnnotationModeChange={canAnnotate ? setAnnotationMode : undefined}
        onStampChange={setSelectedStamp}
        onSearchChange={setSearchQuery}
        onRequestSign={canSign ? onRequestSign : undefined}
        onRequestExport={onRequestExport}
      />

      <Paper
        withBorder
        p={0}
        style={{
          overflow: 'auto',
          maxHeight: 'calc(100vh - 200px)',
          display: 'flex',
          justifyContent: 'center',
          backgroundColor: 'var(--mantine-color-gray-1)',
        }}
      >
        <Document
          file={url}
          onLoadSuccess={onDocumentLoadSuccess as (pdf: { numPages: number }) => void}
          onLoadError={onDocumentLoadError}
          loading={
            <Center py="xl">
              <Loader />
              <Text ml="sm" size="sm" c="dimmed">Loading PDF...</Text>
            </Center>
          }
        >
          <div style={{ position: 'relative' }}>
            <Page
              pageNumber={viewerState.currentPage}
              scale={viewerState.scale}
              renderTextLayer
              renderAnnotationLayer
            />
            <AnnotationLayer
              annotations={annotations.filter(
                (a) => a.page === viewerState.currentPage,
              )}
              page={viewerState.currentPage}
              scale={viewerState.scale}
              mode={viewerState.annotationMode}
              selectedStamp={viewerState.selectedStamp}
              onAddAnnotation={canAnnotate ? handleAddAnnotation : undefined}
              onRemoveAnnotation={canAnnotate ? handleRemoveAnnotation : undefined}
            />
          </div>
        </Document>
      </Paper>
    </Stack>
  );
}

// ============================================================================
// Full Variant — 3-panel suite with all annotation tools
// ============================================================================

function FullPDFViewer({
  url,
  annotations,
  onAnnotationsChange,
  onRequestSign,
  onRequestExport,
  canAnnotate = false,
  canSign = false,
  fileName,
  onXfaFlattened,
}: Omit<PDFViewerProps, 'variant' | 'watermark'>) {
  const { state, setNumPages, addAnnotation, removeAnnotation, isPro } = usePdfSuite();
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pdfProxy, setPdfProxy] = useState<unknown>(null);
  const [isXfa, setIsXfa] = useState(false);
  const { viewer } = state;

  const onDocumentLoadSuccess = useCallback(
    async (result: { numPages: number }) => {
      setNumPages(result.numPages);
      setLoadError(null);
      setPdfProxy(result);

      // Detect XFA forms via first-page text heuristic
      try {
        const proxy = result as unknown as { getPage: (n: number) => Promise<{ getTextContent: () => Promise<{ items: Array<{ str: string }> }> }> };
        if (proxy.getPage) {
          const page = await proxy.getPage(1);
          const textContent = await page.getTextContent();
          const text = textContent.items.map((item) => item.str).join(' ');
          if (text.includes('This PDF form cannot be displayed') || text.includes('Open With Adobe Reader')) {
            setIsXfa(true);
          }
        }
      } catch {
        // Detection failed — not critical
      }
    },
    [setNumPages],
  );

  const onDocumentLoadError = useCallback((error: Error) => {
    setLoadError(error.message);
  }, []);

  // Sync context annotations back to parent
  const handleAnnotationAdd = useCallback(
    (annotation: PdfAnnotation) => {
      addAnnotation(annotation);
      onAnnotationsChange([...annotations, annotation]);
    },
    [addAnnotation, annotations, onAnnotationsChange],
  );

  const handleAnnotationRemove = useCallback(
    (id: string) => {
      removeAnnotation(id);
      onAnnotationsChange(annotations.filter((a) => a.id !== id));
    },
    [removeAnnotation, annotations, onAnnotationsChange],
  );

  if (loadError) {
    return (
      <Alert icon={<IconAlertTriangle size={16} />} color="red" title="PDF Load Error">
        Failed to load PDF: {loadError}
      </Alert>
    );
  }

  const currentAnnotations = state.annotations.filter((a) => a.page === viewer.currentPage);

  const toolbar = (
    <EnhancedPdfToolbar
      canAnnotate={canAnnotate}
      canSign={canSign}
      onRequestSign={onRequestSign}
      onRequestExport={onRequestExport}
    />
  );

  // XFA forms can't be rendered by pdf.js — show dedicated panel instead of broken content
  if (isXfa) {
    return <XfaDetectedPanel fileName={fileName} onXfaFlattened={onXfaFlattened} />;
  }

  return (
    <>
      {isPro && <KeyboardShortcutHandler />}
      <PdfSuiteShell toolbar={toolbar} pdfDocumentProxy={pdfProxy}>
        <Document
          file={url}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={
            <Center py="xl">
              <Loader />
              <Text ml="sm" size="sm" c="dimmed">Loading PDF...</Text>
            </Center>
          }
        >
          <div style={{ position: 'relative' }}>
            <Page
              pageNumber={viewer.currentPage}
              scale={viewer.scale}
              renderTextLayer
              renderAnnotationLayer
            />

            {/* Watermark overlay (pro) */}
            {isPro && <WatermarkOverlay />}

            {/* Base annotation layer — highlight + text are free, stamp is pro */}
            <AnnotationLayer
              annotations={currentAnnotations.filter((a) =>
                a.type === 'highlight' || a.type === 'text' || (isPro && a.type === 'stamp'),
              )}
              page={viewer.currentPage}
              scale={viewer.scale}
              mode={viewer.annotationMode}
              selectedStamp={viewer.selectedStamp}
              onAddAnnotation={canAnnotate ? handleAnnotationAdd : undefined}
              onRemoveAnnotation={canAnnotate ? handleAnnotationRemove : undefined}
            />

            {/* Pro-only annotation overlays */}
            {isPro && (
              <>
                <ShapeOverlay
                  page={viewer.currentPage}
                  scale={viewer.scale}
                  annotations={currentAnnotations}
                />
                <InkCanvas page={viewer.currentPage} scale={viewer.scale} />
                <StickyNoteLayer
                  page={viewer.currentPage}
                  scale={viewer.scale}
                  annotations={currentAnnotations}
                />
                <RedactionToolOverlay
                  page={viewer.currentPage}
                  scale={viewer.scale}
                  annotations={currentAnnotations}
                />
                <MiniMap />
              </>
            )}

            {/* Text search — free for all tiers */}
            <TextSearchHighlight pdfDocumentProxy={pdfProxy} />
          </div>
        </Document>
      </PdfSuiteShell>
    </>
  );
}
